import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { CheckCircle, AlertTriangle, XCircle, Eye } from 'lucide-react';

interface PageMeta {
  route: string;
  title: string;
  description: string;
  titleLength: number;
  descriptionLength: number;
  issues: string[];
  recommendations: string[];
}

const SEOMetaAuditor = () => {
  const [pageMetaData, setPageMetaData] = useState<PageMeta[]>([]);
  const [currentPageMeta, setCurrentPageMeta] = useState<PageMeta | null>(null);

  // Audit current page meta
  const auditCurrentPage = () => {
    const titleElement = document.querySelector('title');
    const descriptionElement = document.querySelector('meta[name="description"]');
    
    const title = titleElement?.textContent || '';
    const description = descriptionElement?.getAttribute('content') || '';
    const route = window.location.pathname;

    const issues: string[] = [];
    const recommendations: string[] = [];

    // Title validation
    if (!title) {
      issues.push('Missing title tag');
    } else if (title.length < 30) {
      issues.push('Title too short (< 30 characters)');
      recommendations.push('Expand title to include more descriptive keywords');
    } else if (title.length > 60) {
      issues.push('Title too long (> 60 characters)');
      recommendations.push('Shorten title to prevent truncation in search results');
    }

    if (!title.includes('SeniorThrive')) {
      recommendations.push('Consider including "SeniorThrive" brand name in title');
    }

    // Description validation
    if (!description) {
      issues.push('Missing meta description');
    } else if (description.length < 120) {
      issues.push('Description too short (< 120 characters)');
      recommendations.push('Expand description to better explain page value');
    } else if (description.length > 160) {
      issues.push('Description too long (> 160 characters)');
      recommendations.push('Shorten description to prevent truncation in search results');
    }

    // Check for duplicate H1 tags
    const h1Elements = document.querySelectorAll('h1');
    if (h1Elements.length === 0) {
      issues.push('Missing H1 tag');
    } else if (h1Elements.length > 1) {
      issues.push(`Multiple H1 tags found (${h1Elements.length})`);
      recommendations.push('Use only one H1 tag per page for better SEO structure');
    }

    // Check for images without alt text
    const images = document.querySelectorAll('img:not([alt]), img[alt=""]');
    if (images.length > 0) {
      issues.push(`${images.length} images missing alt text`);
      recommendations.push('Add descriptive alt text to all images for accessibility and SEO');
    }

    const pageMeta: PageMeta = {
      route,
      title,
      description,
      titleLength: title.length,
      descriptionLength: description.length,
      issues,
      recommendations
    };

    setCurrentPageMeta(pageMeta);
  };

  // Audit common SeniorThrive pages
  const auditAllPages = async () => {
    const commonPages = [
      { route: '/', expectedTitle: 'SeniorThrive - Empowering Older Adults to Thrive at Home' },
      { route: '/older-adults', expectedTitle: 'Older Adults - Safe & Independent Living at Home | SeniorThrive' },
      { route: '/thrivefamily', expectedTitle: 'Family Members - Support Your Loved Ones at Home | SeniorThrive' },
      { route: '/caregivers', expectedTitle: 'Caregivers - Professional Tools & Support | SeniorThrive' },
      { route: '/pricing', expectedTitle: 'Pricing - Choose Your SeniorThrive Plan | SeniorThrive' },
      { route: '/features', expectedTitle: 'Features - Smart Senior Care Platform | SeniorThrive' },
      
      { route: '/support', expectedTitle: 'Support - Help & Resources | SeniorThrive' },
      { route: '/blog', expectedTitle: 'Blog - Senior Care Tips & Insights | SeniorThrive' }
    ];

    const auditResults: PageMeta[] = [];

    for (const page of commonPages) {
      const issues: string[] = [];
      const recommendations: string[] = [];

      // Check title length
      if (page.expectedTitle.length > 60) {
        issues.push('Title may be too long for optimal SERP display');
        recommendations.push('Consider shortening title while keeping key information');
      }

      // Add generic recommendations
      if (!page.expectedTitle.includes('SeniorThrive')) {
        recommendations.push('Title includes brand name - good for brand recognition');
      }

      auditResults.push({
        route: page.route,
        title: page.expectedTitle,
        description: 'Meta description audit requires page visit',
        titleLength: page.expectedTitle.length,
        descriptionLength: 0,
        issues,
        recommendations: [...recommendations, 'Visit page for complete description audit']
      });
    }

    setPageMetaData(auditResults);
  };

  useEffect(() => {
    auditCurrentPage();
  }, []);

  const getScoreColor = (issues: string[]) => {
    if (issues.length === 0) return 'text-green-600';
    if (issues.length <= 2) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getScoreIcon = (issues: string[]) => {
    if (issues.length === 0) return <CheckCircle className="h-4 w-4 text-green-600" />;
    if (issues.length <= 2) return <AlertTriangle className="h-4 w-4 text-yellow-600" />;
    return <XCircle className="h-4 w-4 text-red-600" />;
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Eye className="h-5 w-5" />
            SEO Meta Tag Auditor
          </CardTitle>
          <CardDescription>
            Analyze title tags, meta descriptions, and heading structure for SEO optimization.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-2">
            <Button onClick={auditCurrentPage} variant="outline">
              Audit Current Page
            </Button>
            <Button onClick={auditAllPages}>
              Audit All Pages
            </Button>
          </div>

          {/* Current Page Analysis */}
          {currentPageMeta && (
            <div className="space-y-4">
              <h3 className="font-bold text-lg">Current Page Analysis</h3>
              <Card>
                <CardContent className="pt-6">
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="font-medium">Route: {currentPageMeta.route}</span>
                      <div className="flex items-center gap-2">
                        {getScoreIcon(currentPageMeta.issues)}
                        <span className={getScoreColor(currentPageMeta.issues)}>
                          {currentPageMeta.issues.length === 0 ? 'Perfect' : `${currentPageMeta.issues.length} issues`}
                        </span>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <div>
                        <span className="text-sm font-medium">Title ({currentPageMeta.titleLength} chars):</span>
                        <p className="text-sm bg-muted p-2 rounded mt-1">{currentPageMeta.title}</p>
                        <Badge variant={currentPageMeta.titleLength >= 30 && currentPageMeta.titleLength <= 60 ? 'default' : 'destructive'}>
                          {currentPageMeta.titleLength >= 30 && currentPageMeta.titleLength <= 60 ? 'Good length' : 'Needs adjustment'}
                        </Badge>
                      </div>

                      <div>
                        <span className="text-sm font-medium">Description ({currentPageMeta.descriptionLength} chars):</span>
                        <p className="text-sm bg-muted p-2 rounded mt-1">
                          {currentPageMeta.description || 'No meta description found'}
                        </p>
                        <Badge variant={currentPageMeta.descriptionLength >= 120 && currentPageMeta.descriptionLength <= 160 ? 'default' : 'destructive'}>
                          {currentPageMeta.descriptionLength >= 120 && currentPageMeta.descriptionLength <= 160 ? 'Good length' : 'Needs adjustment'}
                        </Badge>
                      </div>
                    </div>

                    {currentPageMeta.issues.length > 0 && (
                      <Alert>
                        <AlertTriangle className="h-4 w-4" />
                        <AlertDescription>
                          <div className="space-y-1">
                            <span className="font-medium">Issues found:</span>
                            <ul className="list-disc list-inside text-sm">
                              {currentPageMeta.issues.map((issue, index) => (
                                <li key={index}>{issue}</li>
                              ))}
                            </ul>
                          </div>
                        </AlertDescription>
                      </Alert>
                    )}

                    {currentPageMeta.recommendations.length > 0 && (
                      <Alert>
                        <CheckCircle className="h-4 w-4" />
                        <AlertDescription>
                          <div className="space-y-1">
                            <span className="font-medium">Recommendations:</span>
                            <ul className="list-disc list-inside text-sm">
                              {currentPageMeta.recommendations.map((rec, index) => (
                                <li key={index}>{rec}</li>
                              ))}
                            </ul>
                          </div>
                        </AlertDescription>
                      </Alert>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* All Pages Analysis */}
          {pageMetaData.length > 0 && (
            <div className="space-y-4">
              <h3 className="font-bold text-lg">Site-wide Meta Analysis</h3>
              <div className="grid gap-4">
                {pageMetaData.map((page, index) => (
                  <Card key={index}>
                    <CardContent className="pt-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium">{page.route}</span>
                        <div className="flex items-center gap-2">
                          {getScoreIcon(page.issues)}
                          <span className={getScoreColor(page.issues)}>
                            {page.issues.length === 0 ? 'Good' : `${page.issues.length} issues`}
                          </span>
                        </div>
                      </div>
                      <div className="text-sm space-y-1">
                        <p><span className="font-medium">Title:</span> {page.title} ({page.titleLength} chars)</p>
                        {page.issues.length > 0 && (
                          <div className="text-red-600">
                            <span className="font-medium">Issues:</span> {page.issues.join(', ')}
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SEOMetaAuditor;